import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import NewCustomerButton from '@components/NewCustomerButton';
import { useT, type StringKey } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';
import { useCatalogStore } from '@stores/catalogStore';
import {
  filterCandidates,
  suggestFallback,
  type SmartFallback,
} from '@lib/filterCandidates';
import type {
  BedroomPref,
  BudgetRange,
  Goal,
  InvestmentPriority,
} from '@lib/types';

interface Choice<T> {
  value: T;
  labelKey: StringKey;
  descKey?: StringKey;
}

const GOAL_CHOICES: ReadonlyArray<Choice<Goal>> = [
  { value: 'investment', labelKey: 'goal.investment', descKey: 'goal.investment_desc' },
  { value: 'living', labelKey: 'goal.living', descKey: 'goal.living_desc' },
  { value: 'both', labelKey: 'goal.both', descKey: 'goal.both_desc' },
];

const BUDGET_CHOICES: ReadonlyArray<Choice<BudgetRange>> = [
  { value: 'under_25k', labelKey: 'goal.budget.under25' },
  { value: '25k_40k', labelKey: 'goal.budget.25_40' },
  { value: '40k_50k', labelKey: 'goal.budget.40_50' },
  { value: 'over_50k', labelKey: 'goal.budget.over50' },
];

const BEDROOM_CHOICES: ReadonlyArray<Choice<BedroomPref>> = [
  { value: 1, labelKey: 'goal.beds.1' },
  { value: 2, labelKey: 'goal.beds.2' },
  { value: 3, labelKey: 'goal.beds.3' },
  { value: 'flex', labelKey: 'goal.beds.flex' },
];

const PRIORITY_CHOICES: ReadonlyArray<Choice<InvestmentPriority>> = [
  { value: 'rental', labelKey: 'goal.priority.rental', descKey: 'goal.priority.rental_desc' },
  { value: 'capital', labelKey: 'goal.priority.capital', descKey: 'goal.priority.capital_desc' },
  { value: 'both', labelKey: 'goal.priority.both', descKey: 'goal.priority.both_desc' },
];

export default function Step3Goal() {
  const t = useT();
  const navigate = useNavigate();

  const language = useSessionStore((s) => s.language);
  const goal = useSessionStore((s) => s.goal);
  const budget = useSessionStore((s) => s.budget);
  const bedrooms = useSessionStore((s) => s.bedrooms);
  const priority = useSessionStore((s) => s.priority);
  const setGoal = useSessionStore((s) => s.setGoal);
  const setBudget = useSessionStore((s) => s.setBudget);
  const setBedrooms = useSessionStore((s) => s.setBedrooms);
  const setPriority = useSessionStore((s) => s.setPriority);

  const units = useCatalogStore((s) => s.units);
  const setFilteredUnits = useCatalogStore((s) => s.setFilteredUnits);

  const isAr = language === 'ar';
  const needsPriority = goal === 'investment';

  const allAnswered = Boolean(
    goal && budget && bedrooms !== undefined && (!needsPriority || priority)
  );

  const candidates = useMemo(() => {
    if (!goal || !budget || bedrooms === undefined) return [];
    return filterCandidates(units, {
      goal,
      budget,
      bedrooms,
      priority: needsPriority ? priority : undefined,
    });
  }, [units, goal, budget, bedrooms, priority, needsPriority]);

  const fallback: SmartFallback | null = useMemo(() => {
    if (!allAnswered || candidates.length > 0) return null;
    if (!goal || !budget || bedrooms === undefined) return null;
    return suggestFallback(units, {
      goal,
      budget,
      bedrooms,
      priority: needsPriority ? priority : undefined,
    });
  }, [allAnswered, candidates.length, units, goal, budget, bedrooms, priority, needsPriority]);

  const handleNext = () => {
    if (!allAnswered) return;
    setFilteredUnits(candidates);
    navigate('/match');
  };

  const acceptFallback = (fb: SmartFallback) => {
    if (fb.suggested.bedrooms !== undefined) setBedrooms(fb.suggested.bedrooms);
    if (fb.suggested.budget !== undefined) setBudget(fb.suggested.budget);
    setFilteredUnits(fb.candidates);
    navigate('/match');
  };

  const advisorHandoff = () => {
    // Real WhatsApp/respond.io handoff lands in M8. Placeholder for now.
    window.alert(t('goal.advisorAlert'));
  };

  return (
    <div className="step-container">
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => navigate('/project')}
          className="btn-ghost !px-3 !py-1.5 text-sm"
        >
          {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          {t('common.back')}
        </button>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 text-xs text-navy-400">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
            {t('goal.timeBudget')}
            <span className="num-en">· 2 {t('common.minute')}</span>
          </span>
          <NewCustomerButton />
        </div>
      </div>

      <header className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">
          {t('goal.title')}
        </h1>
        <p className="text-navy-600">{t('goal.subtitle')}</p>
      </header>

      <div className="space-y-8">
        <Question
          number={1}
          title={t('goal.q1')}
          hint={t('goal.q1_hint')}
        >
          <ChoiceGrid
            cols={3}
            choices={GOAL_CHOICES}
            selected={goal}
            onSelect={setGoal}
            t={t}
          />
        </Question>

        {goal && (
          <Question
            number={2}
            title={t('goal.q2')}
            hint={t('goal.q2_hint')}
          >
            <ChoiceGrid
              cols={4}
              choices={BUDGET_CHOICES}
              selected={budget}
              onSelect={setBudget}
              t={t}
            />
          </Question>
        )}

        {goal && budget && (
          <Question
            number={3}
            title={t('goal.q3')}
            hint={t('goal.q3_hint')}
          >
            <ChoiceGrid
              cols={4}
              choices={BEDROOM_CHOICES}
              selected={bedrooms}
              onSelect={setBedrooms}
              t={t}
            />
          </Question>
        )}

        {needsPriority && goal && budget && bedrooms !== undefined && (
          <Question
            number={4}
            title={t('goal.q4')}
            hint={t('goal.q4_hint')}
          >
            <ChoiceGrid
              cols={3}
              choices={PRIORITY_CHOICES}
              selected={priority}
              onSelect={setPriority}
              t={t}
            />
          </Question>
        )}

        {allAnswered && candidates.length > 0 && (
          <ResultBanner count={candidates.length} t={t} />
        )}

        {allAnswered && candidates.length === 0 && fallback && (
          <FallbackBanner
            fallback={fallback}
            language={language}
            onAccept={() => acceptFallback(fallback)}
            t={t}
          />
        )}

        {allAnswered && candidates.length === 0 && !fallback && (
          <AdvisorHandoffBanner onContact={advisorHandoff} t={t} />
        )}
      </div>

      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={handleNext}
          disabled={!allAnswered || candidates.length === 0}
          className="btn-primary"
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}

function Question({
  number,
  title,
  hint,
  children,
}: {
  number: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="animate-slide-up">
      <header className="flex items-baseline gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-navy text-gold font-bold text-sm num-en flex-shrink-0">
          {number}
        </span>
        <div>
          <h2 className="text-lg md:text-xl font-bold text-navy">{title}</h2>
          {hint && <p className="text-xs text-navy-400 mt-0.5">{hint}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

interface ChoiceGridProps<T> {
  cols: 3 | 4;
  choices: ReadonlyArray<Choice<T>>;
  selected: T | undefined;
  onSelect: (value: T) => void;
  t: (k: StringKey) => string;
}

function ChoiceGrid<T extends string | number>({
  cols,
  choices,
  selected,
  onSelect,
  t,
}: ChoiceGridProps<T>) {
  const colClass = cols === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';
  return (
    <div className={`grid grid-cols-2 ${colClass} gap-3`}>
      {choices.map((c) => {
        const active = selected === c.value;
        return (
          <button
            key={String(c.value)}
            type="button"
            onClick={() => onSelect(c.value)}
            aria-pressed={active}
            className={`choice-card ${active ? 'choice-card-active' : ''}`}
          >
            {active && (
              <span className="absolute top-2 end-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold text-navy">
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
            )}
            <span className="font-semibold text-base">{t(c.labelKey)}</span>
            {c.descKey && (
              <span
                className={`text-xs ${active ? 'text-white/75' : 'text-navy-500'}`}
              >
                {t(c.descKey)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ResultBanner({
  count,
  t,
}: {
  count: number;
  t: (k: StringKey) => string;
}) {
  return (
    <div className="animate-slide-up p-4 rounded-xl bg-gold-50 border border-gold/30 flex items-center justify-center gap-2 text-navy">
      <span>{t('goal.foundUnits')}</span>
      <span className="text-2xl font-bold num-en text-gold-700">{count}</span>
      <span>{t('goal.foundUnitsSuffix')}</span>
    </div>
  );
}

function FallbackBanner({
  fallback,
  language,
  onAccept,
  t,
}: {
  fallback: SmartFallback;
  language: 'ar' | 'en';
  onAccept: () => void;
  t: (k: StringKey) => string;
}) {
  return (
    <div className="animate-slide-up p-5 rounded-xl bg-gold-50 border border-gold/40">
      <div className="flex items-start gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gold text-navy font-bold text-sm flex-shrink-0">
          ?
        </span>
        <div>
          <h3 className="font-bold text-navy text-base mb-1">
            {t('goal.fallbackTitle')}
          </h3>
          <p className="text-sm text-navy-700 leading-relaxed">
            {language === 'ar' ? fallback.reason_ar : fallback.reason_en}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 justify-end">
        <button type="button" onClick={onAccept} className="btn-primary !py-2 !px-5 text-sm">
          {t('goal.fallbackYes')}
        </button>
      </div>
      <p className="text-xs text-navy-500 text-center mt-2">
        {t('goal.fallbackNo')}
      </p>
    </div>
  );
}

function AdvisorHandoffBanner({
  onContact,
  t,
}: {
  onContact: () => void;
  t: (k: StringKey) => string;
}) {
  return (
    <div className="animate-slide-up p-5 rounded-xl bg-navy-50 border border-navy-100">
      <h3 className="font-bold text-navy text-base mb-1">
        {t('goal.advisorTitle')}
      </h3>
      <p className="text-sm text-navy-600 leading-relaxed mb-3">
        {t('goal.advisorBody')}
      </p>
      <div className="flex justify-end">
        <button type="button" onClick={onContact} className="btn-gold !py-2 !px-5 text-sm">
          {t('goal.advisorBtn')}
        </button>
      </div>
    </div>
  );
}
